import type { NavItem } from './filterNavItems';

/** Sección del instructivo de cada pantalla diaria: /instructivos?seccion=<slug>. */
export type SeccionDiaria =
  | 'hoy' | 'proveedores' | 'ordenes-de-compra' | 'remitos' | 'facturas' | 'pagos'
  | 'movimientos-de-stock' | 'conteos-de-inventario' | 'devoluciones'
  | 'base-y-publicaciones' | 'preguntas-ml' | 'formacion-de-precios';

/** Secciones del instructivo a las que enlaza un «Ver instructivo»: las pantallas diarias y las de configuración. */
export type SeccionConEnlace = SeccionDiaria | 'variantes-por-color';

export interface PantallaDiaria extends NavItem {
  slug: SeccionDiaria;
}

export interface BloqueDiario {
  label: 'Compras' | 'Stock' | 'Publicaciones';
  items: PantallaDiaria[];
}

/** "Hoy": primera del menú Día a día y la pantalla de entrada. La ven todos; cada sección pide su permiso. */
export const HOY: PantallaDiaria = {
  slug: 'hoy', path: '/hoy', label: 'Hoy',
  icon: 'M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z',
};

/**
 * Las pantallas de todos los días, en el orden en que se usan.
 * Lo comparten el menú ("Día a día") y el índice de Instructivos: los nombres coinciden con el título de cada pantalla.
 */
export const diaADia: BloqueDiario[] = [
  {
    label: 'Compras',
    items: [
      { slug: 'proveedores', path: '/proveedores', label: 'Proveedores', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z', permisosRequeridos: ['proveedores:ver'] },
      { slug: 'ordenes-de-compra', path: '/compras', label: 'Órdenes de compra', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2', permisosRequeridos: ['compras:ver'] },
      { slug: 'remitos', path: '/remitos', label: 'Remitos', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z', permisosRequeridos: ['remitos:ver'] },
      { slug: 'facturas', path: '/facturas', label: 'Facturas', icon: 'M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z', permisosRequeridos: ['facturas:ver'] },
      { slug: 'pagos', path: '/pagos', label: 'Pagos', icon: 'M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z', permisosRequeridos: ['pagos:ver'] },
    ],
  },
  {
    label: 'Stock',
    items: [
      { slug: 'movimientos-de-stock', path: '/movimientos-stock', label: 'Movimientos de stock', icon: 'M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4', permisosRequeridos: ['wms:ver'] },
      { slug: 'conteos-de-inventario', path: '/conteos-inventario', label: 'Conteos de inventario', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01', permisosRequeridos: ['wms:ver'] },
      { slug: 'devoluciones', path: '/devoluciones', label: 'Devoluciones', icon: 'M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6', permisosRequeridos: ['ventas:ver'] },
    ],
  },
  {
    label: 'Publicaciones',
    items: [
      { slug: 'base-y-publicaciones', path: '/base-publicaciones', label: 'Base y publicaciones', icon: 'M4 7v10c0 2 1 3 3 3h10c2 0 3-1 3-3V7M4 7c0-2 1-3 3-3h10c2 0 3 1 3 3M4 7h16M9 3v4m6-4v4', permisosRequeridos: ['catalogo:ver'] },
      { slug: 'preguntas-ml', path: '/preguntas-ml', label: 'Preguntas ML', icon: 'M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z', permisosRequeridos: ['preguntas:ver'] },
      { slug: 'formacion-de-precios', path: '/formacion-precios', label: 'Formación de precios', icon: 'M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 11h.01M12 11h.01M15 11h.01M4 19h16a2 2 0 002-2V7a2 2 0 00-2-2H4a2 2 0 00-2 2v10a2 2 0 002 2zM3 10h18', permisosRequeridos: ['catalogo:ver'] },
    ],
  },
];

/** Cómo se llega desde el menú: "Día a día → Compras → Remitos". */
export function donde(slug: SeccionDiaria): string {
  if (slug === HOY.slug) return `Día a día → ${HOY.label}`;
  for (const bloque of diaADia) {
    const item = bloque.items.find((i) => i.slug === slug);
    if (item) return `Día a día → ${bloque.label} → ${item.label}`;
  }
  throw new Error(`Pantalla diaria desconocida: ${slug}`);
}
